import type { FC } from "react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import appConfig from "config/appConfig";
import { useTranslation } from "react-i18next";

import DollarIcon from "assets/icons/outlined/dollar.svg?react";

import { CardWrapper, PaymentStatBlock } from "shared/Components";
import { useSoccerPayment } from "shared/hooks";
import type { CurrencyCode, PaymentAccountMetricsModel } from "shared/types";

import type { MenuDotsOption } from "shared/uikit";
import { Button, InfoIcon, Text } from "shared/uikit";

import { InfoIconType } from "shared/uikit/InfoIcon";
import { ButtonSizesEnum, ButtonVariantsEnum, TextVariantsEnum } from "shared/uikit/types";

import type { PaymentAccountStripeBalanceModel } from "../../types/PaymentModel";

const WithdrawMoneyDialog = lazy(() => import("shared/Components/WithdrawMoneyDialog"));
const BusinessCountryModal = lazy(() => import("shared/Components/BusinessCountryModal"));

type CurrencyPaymentMetrics = Partial<Omit<PaymentAccountMetricsModel, "currencies">> & {
	currency?: CurrencyCode;
};

interface PaymentMetricsProps {
	objectId: string;
	paymentAccountExists?: boolean;
	requestingRegistration?: boolean;
	onRegisterPaymentAccount?: (countryCode: string) => void;
	allowViewStripeDashboard?: boolean;
	stripeAccountValid?: boolean;
}

const PaymentMetrics: FC<PaymentMetricsProps> = ({
	objectId,
	paymentAccountExists,
	requestingRegistration,
	onRegisterPaymentAccount,
	allowViewStripeDashboard,
	stripeAccountValid
}) => {
	const { t } = useTranslation();

	const { getPaymentAccountMetrics, getData: getSoccerPaymentData } = useSoccerPayment();
	const { loadingPaymentAccountMetrics, paymentAccountMetrics } = getSoccerPaymentData();

	const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
	const [showBusinessCountryModal, setShowBusinessCountryModal] = useState(false);

	const fetchData = useCallback(
		(objectId: string) => {
			getPaymentAccountMetrics({ objectId });
		},
		[getPaymentAccountMetrics]
	);

	const registerPaymentAccount = useCallback(() => {
		setShowBusinessCountryModal(true);
	}, []);

	useEffect(() => {
		fetchData(objectId);
	}, [fetchData, objectId]);

	// const balanceOptions: MenuDotsOption[] = useMemo(
	// 	() =>
	// 		paymentAccountMetrics?.balance?.total
	// 			? [{ name: "Withdraw amount", onClick: () => setWithdrawDialogOpen(true) }]
	// 			: [],
	// 	[paymentAccountMetrics?.balance?.total]
	// );

	const balanceOptions: MenuDotsOption[] = useMemo(() => [], []);

	const stripeStatusLabel = useMemo(() => {
		if (!paymentAccountExists) {
			return "Not connected";
		}

		return stripeAccountValid ? "Connected" : "Action required";
	}, [paymentAccountExists, stripeAccountValid]);

	const currencyMetrics = useMemo<CurrencyPaymentMetrics[]>(() => {
		if (paymentAccountMetrics?.currencies?.length) {
			return paymentAccountMetrics.currencies;
		}

		if (paymentAccountMetrics) {
			const { inbound, outbound, balance } = paymentAccountMetrics;
			return [{ inbound, outbound, balance }];
		}

		return [{}];
	}, [paymentAccountMetrics]);

	const handleCloseCountryModal = (countryCode?: string) => {
		setShowBusinessCountryModal(false);
		if (countryCode) {
			onRegisterPaymentAccount && onRegisterPaymentAccount(countryCode);
		}
	};

	const getBalanceAmount = useCallback(
		(balance?: { total: number; previousPeriod: number; stripeBalance?: null | PaymentAccountStripeBalanceModel }) => {
			const stripeBalance = balance?.stripeBalance?.available?.length
				? balance.stripeBalance?.available[0]?.amount || 0
				: 0;
			return balance?.total || stripeBalance || 0;
		},
		[]
	);

	return (
		<>
			<div className="flex flex-col gap-4">
				{!!allowViewStripeDashboard && (
					<CardWrapper
						className={clsx(
							"flex flex-col gap-3 border-gray-200 shadow-sm md:flex-row md:items-center md:justify-between",
							paymentAccountExists && !stripeAccountValid && "border-red-500 bg-red-50"
						)}
					>
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<Text variants={TextVariantsEnum.H7} className="text-primary-600">
									{t("payment:payment_account")}
								</Text>
								<span
									className={clsx(
										"rounded-full px-2 py-0.5 text-xs font-medium",
										!paymentAccountExists && "bg-gray-100 text-hint",
										paymentAccountExists && stripeAccountValid && "bg-primary-50 text-primary-600",
										paymentAccountExists && !stripeAccountValid && "bg-red-100 text-red-600"
									)}
								>
									{stripeStatusLabel}
								</span>
								{paymentAccountExists && !stripeAccountValid && (
									<div className="relative z-[1]">
										<InfoIcon
											text={t("payment:stripe_connect_account_issue")}
											placement={"bottom"}
											type={InfoIconType.WARNING}
										/>
									</div>
								)}
							</div>
							<Text variants={TextVariantsEnum.BodySmall} className="mt-1 text-hint">
								Stripe Connect dashboard and payouts setup
							</Text>
						</div>
						{!paymentAccountExists && !!onRegisterPaymentAccount && (
							<Button
								size={ButtonSizesEnum.SM}
								variant={ButtonVariantsEnum.OUTLINED}
								startIcon={<DollarIcon className="svg-paths:fill-primary-500" />}
								className="w-full text-primary-500 md:w-auto"
								onClick={registerPaymentAccount}
								loading={requestingRegistration}
							>
								{t("register")}
							</Button>
						)}
						{paymentAccountExists && (
							<Button
								size={ButtonSizesEnum.SM}
								variant={ButtonVariantsEnum.OUTLINED}
								startIcon={<DollarIcon className="svg-paths:fill-primary-500" />}
								className="w-full text-primary-500 md:w-auto"
								link={{
									to: appConfig.GLOBAL_CONSTANTS.STRIPE_CONNECT_ACCOUNT_DASHBOARD,
									target: "_blank"
								}}
							>
								{t("payment:stripe_dashboard")}
							</Button>
						)}
					</CardWrapper>
				)}
				{currencyMetrics.map(metrics => (
					<div key={metrics.currency ?? "default"} className="flex flex-col gap-3">
						<div className="flex items-center justify-between border-b border-gray-100 pb-2">
							<div className="flex items-center gap-2">
								<Text variants={TextVariantsEnum.Caption2} className="text-hint uppercase">
									{metrics.currency ? t("currency") : t("payment:payment_metrics")}
								</Text>
								{!!metrics.currency && (
									<span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold uppercase text-primary-600">
										{metrics.currency}
									</span>
								)}
							</div>
							<Text variants={TextVariantsEnum.Caption2} className="text-hint">
								{t("payment:last_month_comparison")}
							</Text>
						</div>
						<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
							<PaymentStatBlock
								loading={loadingPaymentAccountMetrics}
								title={t("payment:cash_in")}
								currentAmount={metrics.inbound?.currentPeriod}
								previousAmount={metrics.inbound?.previousPeriod}
								currency={metrics.currency}
								disabled={!paymentAccountExists}
							/>
							<PaymentStatBlock
								loading={loadingPaymentAccountMetrics}
								title={t("payment:cash_out")}
								currentAmount={metrics.outbound?.currentPeriod}
								previousAmount={metrics.outbound?.previousPeriod}
								currency={metrics.currency}
								inverted
								disabled={!paymentAccountExists}
							/>
							<PaymentStatBlock
								loading={loadingPaymentAccountMetrics}
								title={t("payment:balance")}
								currentAmount={getBalanceAmount(metrics.balance)}
								previousAmount={metrics.balance?.previousPeriod}
								currency={metrics.currency}
								opts={balanceOptions}
								disabled={!paymentAccountExists}
							/>
						</div>
					</div>
				))}
			</div>
			{withdrawDialogOpen && !!paymentAccountMetrics?.balance?.total && (
				<Suspense>
					<WithdrawMoneyDialog
						balance={paymentAccountMetrics.balance.total}
						onClose={() => setWithdrawDialogOpen(false)}
					/>
				</Suspense>
			)}
			{showBusinessCountryModal && (
				<Suspense>
					<BusinessCountryModal onClose={handleCloseCountryModal} />
				</Suspense>
			)}
		</>
	);
};

export default PaymentMetrics;
